import { MutableRefObject, useCallback, useEffect } from 'react';
import {
  useLocation,
  useNavigate,
  useNavigationType,
  useSearchParams,
} from 'react-router-dom';

import { useLoggingMutation, useLoggingQuery } from 'lib/apollo/apollo-hooks';
import {
  INSERT_ACTIVITIES_SECTION,
  UPDATE_ACTIVITIES_SECTION,
  DELETE_ACTIVITIES_SECTION,
  GET_ACTIVITIES_SECTIONS,
} from './lesson-builder.graphql';
import {
  InsertActivitiesSection,
  InsertActivitiesSectionVariables,
} from './__generated__/InsertActivitiesSection';
import {
  UpdateActivitiesSection,
  UpdateActivitiesSectionVariables,
} from './__generated__/UpdateActivitiesSection';
import {
  InsertActivitiesSectionInput,
  UpdateActivitiesSectionInput,
  DeleteActivitiesSectionInput,
} from '../../../__generated__/globalTypes';
import {
  DeleteActivitiesSection,
  DeleteActivitiesSectionVariables,
} from './__generated__/DeleteActivitiesSection';
import {
  upsertSectionOverview,
  addSectionOverviewToLesson,
  removeSectionOverviewFromLesson,
  modifySubSectionOverviewSort,
  resetInteractiveContentInCache,
  modifyPageTypeInCache,
  modifyActivitiesPublishChanges,
} from './activity-builder.apollo.utils';
import { Differentiation } from 'approot/shared/differentiation/differentiation.constants';
import { useActivityBuilderApolloContext } from './activity-builder.apollo.context';
import { useGetInteractiveLessonDataFromCache } from './activity-builder.hooks';
import {
  ExtraSectionIdentifiers,
  useActivityBuilderApollo,
} from './activity-builder.apollo';
import { WatchQueryFetchPolicy } from '@apollo/client';
import useUserData from 'approot/shared/user/user-data';
import {
  GetActivitiesSections,
  GetActivitiesSectionsVariables,
} from './__generated__/GetActivitiesSections';
import {
  ACTIVITIES_PAGE_TYPE_TEACHER,
  ASSESSMENT_ACTIVITIES,
  ActivitiesContentType,
  LANGUAGE_CODE_EN_AU,
} from 'global/constants';
import { getStudentClassToDoClassId } from 'approot/students/logic/get-student-class-to-do-class-id';
import { studentAuthApolloContextOption } from 'approot/student-class-to-do/student-class-to-do.utils';
import { InquisitiveLanguageCodes } from 'global/graphql/scalars.type';
import { useActivityBuilderRequest } from './activity-builder-request.apollo';

type CommonProps = {
  onError?: () => void;
};

type CreateNewSectionProps = {
  contentId: number;
  contentType: ActivitiesContentType;
  onCompleted?: (data: InsertActivitiesSection) => void;
} & CommonProps;

export const useCreateNewSection = ({
  contentId,
  contentType,
  onCompleted,
  onError,
}: CreateNewSectionProps) => {
  const { createRequest, clearRequest } = useActivityBuilderRequest();

  const { getInteractiveContent } = useGetInteractiveLessonDataFromCache();
  const {
    interactiveLessonClone,
    setInteractiveLessonClone,
  } = useActivityBuilderApolloContext();

  const [addSection, { data, loading, error, client }] = useLoggingMutation<
    InsertActivitiesSection,
    InsertActivitiesSectionVariables
  >(INSERT_ACTIVITIES_SECTION, {
    refetchQueries: [
      'GetLessonTeachingAndLearningSequence',
      'GetAssessmentTeachingAndLearningSequence',
    ],
    onCompleted: result => {
      if (interactiveLessonClone) {
        setInteractiveLessonClone(null);
      }
      onCompleted?.(result);
      modifyActivitiesPublishChanges({
        client,
        id: contentId,
        contentType,
      });
    },
    onError: () => {
      resetInteractiveContentInCache({
        client,
        contentType,
        interactiveContent: interactiveLessonClone,
      });
      onError?.();
    },
  });

  const createSection = useCallback(
    (input: InsertActivitiesSectionInput) => {
      const requestId = createRequest(input.sectionId);
      const interactiveLesson = getInteractiveContent(contentId, contentType);
      if (!interactiveLesson) return;

      // pageType should always be null for assessments
      if (contentType === ASSESSMENT_ACTIVITIES) {
        input.pageType = null;
      }

      setInteractiveLessonClone(interactiveLesson);

      if (input.parentSectionId) {
        const parentSectionOverview = interactiveLesson.sectionsOverview.find(
          s => s.id === input.parentSectionId
        );

        if (parentSectionOverview) {
          const subSectionsOverview =
            parentSectionOverview.subSectionsOverview || [];
          upsertSectionOverview({
            client,
            sectionOverview: {
              ...parentSectionOverview,
              subSectionsOverview: [
                ...subSectionsOverview,
                {
                  __typename: 'ActivitiesSectionOverview',
                  id: input.sectionId,
                  differentiation: parentSectionOverview.differentiation,
                  checkPoint: parentSectionOverview.checkPoint,
                  parentSectionId: input.parentSectionId,
                  sort: input.sort,
                  pageType: input.pageType || ACTIVITIES_PAGE_TYPE_TEACHER,
                },
              ],
            },
          });
        }
      } else {
        upsertSectionOverview({
          client,
          sectionOverview: {
            __typename: 'ActivitiesSectionOverview',
            id: input.sectionId,
            sort: input.sort,
            pageType: input.pageType || null,
            differentiation: Differentiation.GreenCircle,
            subSectionsOverview: null,
            checkPoint: false,
          },
        });
        addSectionOverviewToLesson({
          client,
          contentId: interactiveLesson.id,
          sectionId: input.sectionId,
          contentType,
        });
      }

      addSection({
        variables: {
          input,
        },
      })
        .catch(() => {})
        .finally(() => {
          clearRequest(requestId);
        });
    },
    [
      addSection,
      getInteractiveContent,
      client,
      setInteractiveLessonClone,
      contentId,
      contentType,
      createRequest,
      clearRequest,
    ]
  );

  return {
    createSection,
    data,
    loading,
    error,
  };
};

type DeleteSectionProps = {
  contentId: number;
  contentType: ActivitiesContentType;
  onCompleted?: (data: DeleteActivitiesSection) => void;
} & CommonProps;

export const useDeleteSection = ({
  contentId,
  contentType,
  onCompleted,
  onError,
}: DeleteSectionProps) => {
  const { createRequest, clearRequest } = useActivityBuilderRequest();

  const { getInteractiveContent } = useGetInteractiveLessonDataFromCache();
  const {
    interactiveLessonClone,
    setInteractiveLessonClone,
  } = useActivityBuilderApolloContext();

  const [
    doDeleteSection,
    { data, loading, error, client },
  ] = useLoggingMutation<
    DeleteActivitiesSection,
    DeleteActivitiesSectionVariables
  >(DELETE_ACTIVITIES_SECTION, {
    refetchQueries: [
      'GetLessonTeachingAndLearningSequence',
      'GetAssessmentTeachingAndLearningSequence',
    ],
    onCompleted: result => {
      if (interactiveLessonClone) {
        setInteractiveLessonClone(null);
      }
      onCompleted?.(result);
      modifyActivitiesPublishChanges({
        client,
        id: contentId,
        contentType,
      });
    },
    onError: () => {
      resetInteractiveContentInCache({
        client,
        contentType,
        interactiveContent: interactiveLessonClone,
      });
      onError?.();
    },
  });

  const deleteSection = useCallback(
    (input: DeleteActivitiesSectionInput, parentSectionId?: string) => {
      const requestId = createRequest(input.sectionId);
      const interactiveLesson = getInteractiveContent(contentId, contentType);
      if (!interactiveLesson) return;

      setInteractiveLessonClone(interactiveLesson);

      if (parentSectionId) {
        const sectionOverview = interactiveLesson.sectionsOverview.find(
          s => s.id === parentSectionId
        );
        if (sectionOverview) {
          upsertSectionOverview({
            client,
            sectionOverview: {
              ...sectionOverview,
              subSectionsOverview:
                sectionOverview.subSectionsOverview?.filter(
                  s => s.id !== input.sectionId
                ) || null,
            },
          });
        }
      } else {
        removeSectionOverviewFromLesson({
          client,
          contentId: interactiveLesson.id,
          sectionId: input.sectionId,
          contentType,
        });
      }

      doDeleteSection({
        variables: {
          input,
        },
      })
        .catch(() => {})
        .finally(() => {
          clearRequest(requestId);
        });
    },
    [
      doDeleteSection,
      getInteractiveContent,
      client,
      setInteractiveLessonClone,
      contentId,
      contentType,
      createRequest,
      clearRequest,
    ]
  );

  return {
    deleteSection,
    data,
    loading,
    error,
  };
};

type UpdateSectionProps = {
  contentId: number;
  contentType: ActivitiesContentType;
  onCompleted?: (data: UpdateActivitiesSection) => void;
} & CommonProps;

export const useUpdateSection = ({
  contentId,
  contentType,
  onCompleted,
  onError,
}: UpdateSectionProps) => {
  const { createRequest, clearRequest } = useActivityBuilderRequest();

  const { getInteractiveContent } = useGetInteractiveLessonDataFromCache();
  const {
    interactiveLessonClone,
    setInteractiveLessonClone,
  } = useActivityBuilderApolloContext();

  const [addSection, { data, loading, error, client }] = useLoggingMutation<
    UpdateActivitiesSection,
    UpdateActivitiesSectionVariables
  >(UPDATE_ACTIVITIES_SECTION, {
    onCompleted: result => {
      if (interactiveLessonClone) {
        setInteractiveLessonClone(null);
      }
      onCompleted?.(result);
      modifyActivitiesPublishChanges({
        client,
        id: contentId,
        contentType,
      });
    },
    onError: () => {
      resetInteractiveContentInCache({
        client,
        contentType,
        interactiveContent: interactiveLessonClone,
      });
      onError?.();
    },
  });

  const updateSection = useCallback(
    (input: UpdateActivitiesSectionInput) => {
      const requestId = createRequest(input.sectionId);

      const interactiveLesson = getInteractiveContent(contentId, contentType);
      if (!interactiveLesson) return;

      // pageType should always be null for assessments
      if (contentType === ASSESSMENT_ACTIVITIES) {
        input.pageType = null;
      }

      setInteractiveLessonClone(interactiveLesson);

      const sectionsOverview = interactiveLesson?.sectionsOverview || [];
      if (input.parentSectionId) {
        // updating a sub-section
        if (input.sectionId) {
          modifySubSectionOverviewSort({
            client,
            id: input.sectionId,
            sort: input.sort,
          });
        }
      } else {
        const sectionOverviewToUpdate = sectionsOverview.find(
          s => s.id === input.sectionId
        );

        if (sectionOverviewToUpdate) {
          upsertSectionOverview({
            client,
            sectionOverview: {
              ...sectionOverviewToUpdate,
              sort: input.sort,
              pageType: input.pageType || null,
            },
          });
        }

        if (input.pageType) {
          modifyPageTypeInCache({
            client,
            id: input.sectionId,
            pageType: input.pageType,
          });
        }
      }

      addSection({
        variables: {
          input,
        },
      })
        .catch(() => {})
        .finally(() => {
          clearRequest(requestId);
        });
    },
    [
      addSection,
      getInteractiveContent,
      client,
      setInteractiveLessonClone,
      contentId,
      contentType,
      clearRequest,
      createRequest,
    ]
  );

  return {
    updateSection,
    data,
    loading,
    error,
  };
};

type GetInteractiveSectionsProps = {
  contentId: number;
  contentType: ActivitiesContentType;
  sectionIds?: string[];
  fetchPolicy?: WatchQueryFetchPolicy;
  skip?: boolean;
  version?: string | null;
  language?: InquisitiveLanguageCodes;
  isPublic?: boolean;
  onCompleted?: (res: GetActivitiesSections) => void;
};

export const useGetInteractiveLessonSections = ({
  contentId,
  contentType,
  sectionIds,
  fetchPolicy = 'cache-first',
  skip,
  version,
  language = LANGUAGE_CODE_EN_AU,
  isPublic,
  onCompleted,
}: GetInteractiveSectionsProps) => {
  const { isSignedIn, isStudentSignedIn } = useUserData();

  const { data, loading, error } = useLoggingQuery<
    GetActivitiesSections,
    GetActivitiesSectionsVariables
  >(
    GET_ACTIVITIES_SECTIONS,
    {
      context: isStudentSignedIn ? studentAuthApolloContextOption() : undefined,
      skip: !contentId || skip,
      fetchPolicy,
      variables: {
        input: {
          contentId,
          contentType,
          sectionsIds: sectionIds,
          version,
          language,
          public: isPublic,
          // we only need this for student users to get access
          // to signed URLs
          classId: isStudentSignedIn ? getStudentClassToDoClassId() : undefined,
        },
      },
      onCompleted: res => onCompleted?.(res),
    },
    isSignedIn
  );

  return {
    data,
    loading,
    error,
  };
};

export const useUpdateSectionUsingParams = ({
  sortedSections,
  isInitialisedRef,
}: {
  sortedSections: { id: string }[];
  isInitialisedRef: MutableRefObject<boolean>;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const [searchParams] = useSearchParams();
  const section = searchParams.get('page');
  const version = searchParams.get('version');

  const { setActiveSection } = useActivityBuilderApollo();

  useEffect(() => {
    if (
      section === ExtraSectionIdentifiers.Introduction ||
      section === ExtraSectionIdentifiers.Conclusion
    ) {
      setActiveSection(section);
      return;
    }

    // initialise to a particular page on load
    if (sortedSections.length > 0 && !isInitialisedRef.current) {
      if (section) {
        if (sortedSections.find(s => s.id === section)) {
          setActiveSection(section);
        } else {
          // invalid section id
          setActiveSection(ExtraSectionIdentifiers.Introduction);
          const urlWithoutParams = location.pathname;
          if (version) {
            navigate(
              `${urlWithoutParams}?page=${ExtraSectionIdentifiers.Introduction}&version=${version}`
            );
          } else {
            navigate(
              `${urlWithoutParams}?page=${ExtraSectionIdentifiers.Introduction}`
            );
          }
        }
      } else {
        setActiveSection(ExtraSectionIdentifiers.Introduction);
      }
      isInitialisedRef.current = true;
    }
    // eslint-disable-next-line
  }, [sortedSections, section]);

  useEffect(() => {
    // listen for any changes to params (e.g. click browser back button)
    if (navigationType === 'POP') {
      if (section === ExtraSectionIdentifiers.Conclusion) {
        setActiveSection(section);
        return;
      }

      if (section !== ExtraSectionIdentifiers.Introduction) {
        if (section && sortedSections.length > 0) {
          if (sortedSections.find(s => s.id === section)) {
            setActiveSection(section);
          } else {
            // invalid section id
            const urlWithoutParams = location.pathname;
            if (version) {
              navigate(`${urlWithoutParams}?version=${version}`);
            } else {
              navigate(urlWithoutParams);
            }
            setActiveSection(ExtraSectionIdentifiers.Introduction);
          }
        } else {
          setActiveSection(ExtraSectionIdentifiers.Introduction);
        }
      }
    }
    // eslint-disable-next-line
  }, [navigationType, navigate, section]);
};
